import {parseNumber} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import FieldDefinitionLabel from 'registry/field/FieldDefinitionLabel';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import Col from 'ui-library/bs/Col';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {InputErrorMessageStyle, LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import NumericInput from 'ui-library/inputs/NumericInput';
import {NumericInputChangeEvent} from 'util/Types';
import BaseValidationModal, {
  commonStyles,
  ValidationRuleModalProps,
} from 'validation_rules/components/simple_ui/BaseValidationRuleModal';

import {SimpleRule, SimpleRuleInput} from './types';
import useSimpleRules, {SimpleRulesState, ValidationRuleConstantInput} from './useSimpleRules';
import {getInputForSimpleRule} from './utils';

const INTEGER_FIELD_NOT_EQUALS_TEMPLATE_ID = 'svdrltmpl_integerfielddisallowedvalue';
const INTEGER_FIELD_MIN_VALUE_TEMPLATE_ID = 'svdrltmpl_integerfieldminvalue';
const INTEGER_FIELD_MAX_VALUE_TEMPLATE_ID = 'svdrltmpl_integerfieldmaxvalue';

const INTEGER_FIELD_NOT_EQUALS_INPUT_KEY = 'integer_disallowed_value';
const INTEGER_FIELD_MIN_VALUE_INPUT_KEY = 'integer_min_value';
const INTEGER_FIELD_MAX_VALUE_INPUT_KEY = 'integer_max_value';

const FLOAT_FIELD_NOT_EQUALS_TEMPLATE_ID = 'svdrltmpl_floatfielddisallowedvalue';
const FLOAT_FIELD_MIN_VALUE_TEMPLATE_ID = 'svdrltmpl_floatfieldminvalue';
const FLOAT_FIELD_MAX_VALUE_TEMPLATE_ID = 'svdrltmpl_floatfieldmaxvalue';

const FLOAT_FIELD_NOT_EQUALS_INPUT_KEY = 'float_disallowed_value';
const FLOAT_FIELD_MIN_VALUE_INPUT_KEY = 'float_min_value';
const FLOAT_FIELD_MAX_VALUE_INPUT_KEY = 'float_max_value';

interface FieldErrors {
  disallowedValueError?: string;
  minValueError?: string;
  maxValueError?: string;
}

interface NumericFieldModalBaseProps extends ValidationRuleModalProps {
  disallowedValue: string;
  minValue: string;
  maxValue: string;
  handleUpdateSimpleRule: (
    validationRuleTemplateId: string,
    constantInputs: ValidationRuleConstantInput
  ) => void;
  handleRemoveSimpleRule: (validationRuleTemplateId: string) => void;
}

interface NumericFieldModalProps extends NumericFieldModalBaseProps {
  validateInteger: boolean;
  disallowedValueTemplateId: string;
  disallowedValueInputKey: string;
  minValueTemplateId: string;
  minValueInputKey: string;
  maxValueTemplateId: string;
  maxValueInputKey: string;
}

function getFieldErrors(
  disallowedValue: string,
  minValue: string,
  maxValue: string,
  validateInteger: boolean
): FieldErrors {
  const fieldErrors: FieldErrors = {};
  const disallowedValueNumber = parseNumber(disallowedValue);
  const minValueNumber = parseNumber(minValue);
  const maxValueNumber = parseNumber(maxValue);

  if (disallowedValue !== '' && disallowedValueNumber == null) {
    const errorMessage = `The value for 'Cannot equal' must be a number.`;
    fieldErrors.disallowedValueError = errorMessage;
  }
  if (minValue !== '' && minValueNumber == null) {
    const errorMessage = `The value for 'Minimum Value' must be a number.`;
    fieldErrors.minValueError = errorMessage;
  }
  if (maxValue !== '' && maxValueNumber == null) {
    const errorMessage = `The value for 'Maximum Value' must be a number.`;
    fieldErrors.maxValueError = errorMessage;
  }

  if (
    validateInteger &&
    disallowedValueNumber != null &&
    (!Number.isInteger(disallowedValueNumber) || disallowedValue?.includes('.'))
  ) {
    const errorMessage = `The value for 'Cannot equal' must be an integer.`;
    fieldErrors.disallowedValueError = errorMessage;
  }
  if (
    validateInteger &&
    minValueNumber != null &&
    (!Number.isInteger(minValueNumber) || minValue?.includes('.'))
  ) {
    const errorMessage = `The value for 'Minimum Value' must be an integer.`;
    fieldErrors.minValueError = errorMessage;
  }
  if (
    validateInteger &&
    maxValueNumber != null &&
    (!Number.isInteger(maxValueNumber) || maxValue?.includes('.'))
  ) {
    const errorMessage = `The value for 'Maximum Value' must be an integer.`;
    fieldErrors.maxValueError = errorMessage;
  }

  if (minValueNumber != null && maxValueNumber != null && minValueNumber > maxValueNumber) {
    fieldErrors.minValueError = `The value for 'Minimum Value' cannot be greater than 'Maximum Value'.`;
    fieldErrors.maxValueError = `The value for 'Maximum Value' cannot be less than 'Minimum Value'.`;
  }

  if (
    disallowedValueNumber != null &&
    minValueNumber === disallowedValueNumber &&
    maxValueNumber === disallowedValueNumber
  ) {
    fieldErrors.disallowedValueError = `The value for 'Cannot equal' cannot be the same as both 'Minimum Value' and 'Maximum Value'.`;
  }

  return fieldErrors;
}

function NumericFieldModal(props: NumericFieldModalProps): JSX.Element {
  const fieldErrors: FieldErrors = getFieldErrors(
    props.disallowedValue,
    props.minValue,
    props.maxValue,
    props.validateInteger
  );
  const hasErrors =
    Boolean(fieldErrors.disallowedValueError) ||
    Boolean(fieldErrors.minValueError) ||
    Boolean(fieldErrors.maxValueError);

  function onChangeInputValue(
    validationRuleTemplateId: string,
    inputKey: string,
    input: SimpleRuleInput
  ): void {
    // Remove the rule if the input is empty
    if (input === '') {
      props.handleRemoveSimpleRule(validationRuleTemplateId);
    } else {
      props.handleUpdateSimpleRule(validationRuleTemplateId, {
        [inputKey]: input,
      });
    }
  }

  return (
    <BaseValidationModal {...props} disabled={hasErrors}>
      <Row className={css(commonStyles.middleRow)}>
        <Col xs={12} sm={12} md={12} lg={12}>
          <InputWrapper
            id="disallowValue"
            label="Cannot equal"
            labelStyle={LABEL_STYLES.LABEL}
            error={
              fieldErrors && fieldErrors.disallowedValueError
                ? {message: fieldErrors.disallowedValueError, style: InputErrorMessageStyle.ERROR}
                : undefined
            }
          >
            <NumericInput
              validateInteger={props.validateInteger}
              value={props.disallowedValue ?? ''}
              onChange={(e: NumericInputChangeEvent) => {
                onChangeInputValue(
                  props.disallowedValueTemplateId,
                  props.disallowedValueInputKey,
                  e.target.value
                );
              }}
              dataTestAttrs={{component: 'NumericFieldModal', element: 'disallowValueInput'}}
            />
          </InputWrapper>
        </Col>
        <Label xs={12} sm={12} md={12} lg={12} className={css(commonStyles.subtitleLabel)}>
          Data matching this value will be rejected
        </Label>
      </Row>
      <Row>
        <Col xs={6} sm={6} md={6} lg={6} className={css(commonStyles.leftColumn)}>
          <InputWrapper
            id="minValue"
            label="Minimum Value"
            labelStyle={LABEL_STYLES.LABEL}
            error={
              fieldErrors && fieldErrors.minValueError
                ? {message: fieldErrors.minValueError, style: InputErrorMessageStyle.ERROR}
                : undefined
            }
          >
            <NumericInput
              validateInteger={props.validateInteger}
              value={props.minValue ?? ''}
              onChange={(e: NumericInputChangeEvent) => {
                onChangeInputValue(props.minValueTemplateId, props.minValueInputKey, e.target.value);
              }}
              dataTestAttrs={{component: 'NumericFieldModal', element: 'minValueInput'}}
            />
          </InputWrapper>
        </Col>
        <Col xs={6} sm={6} md={6} lg={6} className={css(commonStyles.rightColumn)}>
          <InputWrapper
            id="maxValue"
            label="Maximum Value"
            labelStyle={LABEL_STYLES.LABEL}
            error={
              fieldErrors && fieldErrors.maxValueError
                ? {message: fieldErrors.maxValueError, style: InputErrorMessageStyle.ERROR}
                : undefined
            }
          >
            <NumericInput
              validateInteger={props.validateInteger}
              value={props.maxValue ?? ''}
              onChange={(e: NumericInputChangeEvent) => {
                onChangeInputValue(props.maxValueTemplateId, props.maxValueInputKey, e.target.value);
              }}
              dataTestAttrs={{component: 'NumericFieldModal', element: 'maxValueInput'}}
            />
          </InputWrapper>
        </Col>
        <Label xs={12} sm={12} md={12} lg={12} className={css(commonStyles.subtitleLabel)}>
          Values must be within the range inclusive
        </Label>
      </Row>
    </BaseValidationModal>
  );
}

export function IntegerFieldModal(props: NumericFieldModalBaseProps): JSX.Element {
  return (
    <NumericFieldModal
      {...props}
      validateInteger={true}
      disallowedValueTemplateId={INTEGER_FIELD_NOT_EQUALS_TEMPLATE_ID}
      disallowedValueInputKey={INTEGER_FIELD_NOT_EQUALS_INPUT_KEY}
      minValueTemplateId={INTEGER_FIELD_MIN_VALUE_TEMPLATE_ID}
      minValueInputKey={INTEGER_FIELD_MIN_VALUE_INPUT_KEY}
      maxValueTemplateId={INTEGER_FIELD_MAX_VALUE_TEMPLATE_ID}
      maxValueInputKey={INTEGER_FIELD_MAX_VALUE_INPUT_KEY}
    />
  );
}

export function FloatFieldModal(props: NumericFieldModalBaseProps): JSX.Element {
  return (
    <NumericFieldModal
      {...props}
      validateInteger={false}
      disallowedValueTemplateId={FLOAT_FIELD_NOT_EQUALS_TEMPLATE_ID}
      disallowedValueInputKey={FLOAT_FIELD_NOT_EQUALS_INPUT_KEY}
      minValueTemplateId={FLOAT_FIELD_MIN_VALUE_TEMPLATE_ID}
      minValueInputKey={FLOAT_FIELD_MIN_VALUE_INPUT_KEY}
      maxValueTemplateId={FLOAT_FIELD_MAX_VALUE_TEMPLATE_ID}
      maxValueInputKey={FLOAT_FIELD_MAX_VALUE_INPUT_KEY}
    />
  );
}

interface NumericFieldModalContainerProps {
  schemaField: SchemaField;
  onRequestSubmit: (result: SimpleRulesState) => void;
  onRequestClose: () => void;
  enableMultiSelectOption: boolean;
}

export function IntegerFieldModalContainer(props: NumericFieldModalContainerProps): JSX.Element {
  const [simpleRulesState, simpleRulesActions] = useSimpleRules(props.schemaField);
  const simpleRules = simpleRulesState.simpleRules;
  const legacySimpleRules = simpleRulesState.legacySimpleRules;

  const disallowedValue = getSimpleRuleInputAsString(
    simpleRules,
    INTEGER_FIELD_NOT_EQUALS_TEMPLATE_ID,
    INTEGER_FIELD_NOT_EQUALS_INPUT_KEY
  );
  const minValue = getSimpleRuleInputAsString(
    simpleRules,
    INTEGER_FIELD_MIN_VALUE_TEMPLATE_ID,
    INTEGER_FIELD_MIN_VALUE_INPUT_KEY
  );
  const maxValue = getSimpleRuleInputAsString(
    simpleRules,
    INTEGER_FIELD_MAX_VALUE_TEMPLATE_ID,
    INTEGER_FIELD_MAX_VALUE_INPUT_KEY
  );

  const fieldDefinitionLabel = (
    <Chip
      className={css(styles.fieldDefinitionLabel)}
      text={<FieldDefinitionLabel field={props.schemaField} schemaFieldSelectors={[]} />}
      size={ChipSize.MINI}
    />
  );

  return (
    <IntegerFieldModal
      disallowedValue={disallowedValue}
      minValue={minValue}
      maxValue={maxValue}
      onRequestSubmit={() => {
        props.onRequestSubmit(simpleRulesState);
      }}
      handleUpdateSimpleRule={simpleRulesActions.handleUpdateSimpleRule}
      handleRemoveSimpleRule={simpleRulesActions.handleRemoveSimpleRule}
      onRequestClose={props.onRequestClose}
      legacySimpleRules={legacySimpleRules}
      handleUpdateLegacyRules={simpleRulesActions.handleUpdateLegacySimpleRules}
      fieldDefinitionLabel={fieldDefinitionLabel}
    />
  );
}

export function FloatFieldModalContainer(props: NumericFieldModalContainerProps): JSX.Element {
  const [simpleRulesState, simpleRulesActions] = useSimpleRules(props.schemaField);
  const simpleRules = simpleRulesState.simpleRules;
  const legacySimpleRules = simpleRulesState.legacySimpleRules;

  const disallowedValue = getSimpleRuleInputAsString(
    simpleRules,
    FLOAT_FIELD_NOT_EQUALS_TEMPLATE_ID,
    FLOAT_FIELD_NOT_EQUALS_INPUT_KEY
  );
  const minValue = getSimpleRuleInputAsString(
    simpleRules,
    FLOAT_FIELD_MIN_VALUE_TEMPLATE_ID,
    FLOAT_FIELD_MIN_VALUE_INPUT_KEY
  );
  const maxValue = getSimpleRuleInputAsString(
    simpleRules,
    FLOAT_FIELD_MAX_VALUE_TEMPLATE_ID,
    FLOAT_FIELD_MAX_VALUE_INPUT_KEY
  );

  const fieldDefinitionLabel = (
    <Chip
      className={css(styles.fieldDefinitionLabel)}
      text={<FieldDefinitionLabel field={props.schemaField} schemaFieldSelectors={[]} />}
      size={ChipSize.MINI}
    />
  );

  return (
    <FloatFieldModal
      disallowedValue={disallowedValue}
      minValue={minValue}
      maxValue={maxValue}
      onRequestSubmit={() => {
        props.onRequestSubmit(simpleRulesState);
      }}
      handleUpdateSimpleRule={simpleRulesActions.handleUpdateSimpleRule}
      handleRemoveSimpleRule={simpleRulesActions.handleRemoveSimpleRule}
      onRequestClose={props.onRequestClose}
      legacySimpleRules={legacySimpleRules}
      handleUpdateLegacyRules={simpleRulesActions.handleUpdateLegacySimpleRules}
      fieldDefinitionLabel={fieldDefinitionLabel}
    />
  );
}

const styles = StyleSheet.create({
  fieldDefinitionLabel: {
    marginRight: 0,
  },
});

function getSimpleRuleInputAsString(
  simpleRules: Array<SimpleRule>,
  validationRuleTemplateId: string,
  inputKey: string
): string {
  // We need to convert the value to a string to help maintain ui behavior around decimals
  let value = getInputForSimpleRule(simpleRules, validationRuleTemplateId, inputKey);

  assert(
    value == null || typeof value === 'string' || typeof value === 'number',
    `Expected value to be a string or number or null, got ${typeof value}`
  );

  if (value != null && typeof value === 'number') {
    value = value.toString();
  }

  return value ?? '';
}
